import type { ShopifyImage } from '@repo/shopify';
import type { ApparelBundleRung, ApparelBundleSettings } from '@/lib/apparel-bundle-config';
import {
	earnsAtRung,
	getEntryRungPercent,
	getRungForCount,
	getRungUnitSaving,
} from '@/lib/apparel-bundle-config';

export { getRungForCount };

/** One filled slot: a real product + the variant (size) the shopper picked. */
export interface ApparelBundlePick {
	handle: string;
	productId: string;
	title: string;
	productType: string;
	merchandiseId: string;
	variantTitle: string | null;
	sku?: string | null;
	vendor?: string;
	image: ShopifyImage | null;
	price: number;
	compareAt: number | null;
	currencyCode: string;
	/** True when this pick mirrors a line already in the cart (never re-added). */
	fromCart?: boolean;
	/** True for this PDP's own product, which holds slot one and can't be removed. */
	isCurrent?: boolean;
	/** The cart line, so an existing line can join the bundle group on add. */
	cartLineId?: string;
}

export type ApparelBundleSlots = (ApparelBundlePick | null)[];

export interface ApparelBundlePricing {
	count: number;
	rung: ApparelBundleRung | null;
	nextRung: ApparelBundleRung | null;
	remainingToNext: number;
	/** Sum of the picks' list prices — what they'd pay with no bundle. */
	listTotal: number;
	/** Sum of the per-line savings at the reached rung. */
	saving: number;
	total: number;
	currencyCode: string;
	/** Per-slot saving, index-aligned with the slots array. */
	savingBySlot: (number | null)[];
}

function round2(amount: number): number {
	return Math.round(amount * 100) / 100;
}

export function createEmptySlots(config: ApparelBundleSettings): ApparelBundleSlots {
	return Array.from({ length: config.slotCount }, () => null);
}

/** Per-line saving at a rung: today's price less the rung's percent-off-MSRP price. */
export function getLineSaving(price: number, compareAt: number | null, percentOff: number): number {
	return getRungUnitSaving(price, compareAt, percentOff);
}

/** Whether a pick counts toward a rung — judged at the entry rung, never the reached one. */
export function pickCounts(config: ApparelBundleSettings, pick: ApparelBundlePick): boolean {
	return earnsAtRung(pick.price, pick.compareAt, getEntryRungPercent(config));
}

export function computeApparelBundlePricing(
	config: ApparelBundleSettings,
	slots: ApparelBundleSlots,
	fallbackCurrencyCode: string,
): ApparelBundlePricing {
	const picks = slots.filter((slot): slot is ApparelBundlePick => slot !== null);
	const count = picks.filter((pick) => pickCounts(config, pick)).length;
	const { rung, nextRung } = getRungForCount(config.rungs, count);
	const percentOff = rung?.percentOff ?? 0;

	let listTotal = 0;
	let saving = 0;
	const savingBySlot = slots.map((slot) => {
		if (!slot) return null;
		listTotal += slot.price;
		const lineSaving = getLineSaving(slot.price, slot.compareAt, percentOff);
		saving += lineSaving;
		return lineSaving;
	});

	return {
		count,
		rung,
		nextRung,
		remainingToNext: nextRung ? Math.max(0, nextRung.items - count) : 0,
		listTotal: round2(listTotal),
		saving: round2(saving),
		total: round2(listTotal - saving),
		currencyCode: picks[0]?.currencyCode ?? fallbackCurrencyCode,
		savingBySlot,
	};
}

/** Fill the first open slot; returns the same array when every slot is taken. */
export function fillNextSlot(
	slots: ApparelBundleSlots,
	pick: ApparelBundlePick,
): ApparelBundleSlots {
	const nextIndex = slots.indexOf(null);
	if (nextIndex === -1) return slots;
	const next = [...slots];
	next[nextIndex] = pick;
	return next;
}
